// ═══════════════════════════════════════════════════════════════════════════════
// Employer — Approved task instances list
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import type { KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { AppButton } from '../../components/ui/AppButton';
import { taskInstanceApi } from '../../lib/api';
import type { EmployerApprovedItem } from '@fdh/shared';

export const TaskApprovedListPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [items, setItems] = useState<EmployerApprovedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchApproved = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await taskInstanceApi.approvedList();
      if (response.success && response.data) {
        setItems(response.data.tasks ?? []);
      } else {
        setError(response.error ?? t('task.approvedList.loadError'));
      }
    } catch {
      setError(t('task.approvedList.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void fetchApproved();
  }, [fetchApproved]);

  const handleBack = () => {
    navigate('/tasks');
  };

  const handleOpenDetail = (taskInstanceId: string) => {
    navigate(`/task-instances/${taskInstanceId}`);
  };

  const handleBackKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleBack();
    }
  };

  return (
    <div className="min-h-full bg-gray-50">
      <header className="z-10 bg-white shadow-sm">
        <div className="mx-auto max-w-lg px-4 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={handleBack}
              onKeyDown={handleBackKeyDown}
              tabIndex={0}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label={t('task.action.back')}
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-gray-900">{t('task.approvedList.title')}</h1>
              <p className="text-sm text-gray-500">{t('task.approvedList.subtitle')}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4 py-6 pb-24">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
          </div>
        ) : error ? (
          <div className="rounded-xl bg-red-50 p-6 text-center">
            <p className="text-red-600" role="alert">{error}</p>
            <button
              type="button"
              onClick={() => void fetchApproved()}
              className="mt-3 text-sm font-medium text-red-700 underline hover:no-underline focus:outline-none"
            >
              {t('task.action.refresh')}
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-xl bg-white p-8 text-center shadow-sm">
            <p className="text-gray-600">{t('task.approvedList.empty')}</p>
            <button
              type="button"
              onClick={handleBack}
              className="mt-4 text-sm font-medium text-primary-600 underline hover:no-underline focus:outline-none"
            >
              {t('task.approvedList.backToTasks')}
            </button>
          </div>
        ) : (
          <ul className="space-y-3" role="list">
            {items.map((row) => (
              <li key={row.taskInstanceId}>
                <div className="rounded-xl bg-white p-4 shadow-sm">
                  <h2 className="font-medium text-gray-900">{row.title}</h2>
                  {row.description ? (
                    <p className="mt-1 line-clamp-2 text-sm text-gray-500">{row.description}</p>
                  ) : null}
                  <div className="mt-3 space-y-1 text-xs text-gray-500">
                    <p>
                      {t('task.approvedList.dueDate')}: {row.dueDate}
                      {row.dueTime ? ` · ${row.dueTime}` : ''}
                    </p>
                    <p>
                      {t('task.approvedList.submittedAt')}: {row.submittedAt}
                    </p>
                    {row.reviewedAt ? (
                      <p>
                        {t('task.approvedList.reviewedAt')}: {row.reviewedAt}
                      </p>
                    ) : null}
                  </div>
                  <AppButton
                    type="button"
                    variant="primary"
                    fullWidth
                    className="mt-4 shadow-sm"
                    onClick={() => handleOpenDetail(row.taskInstanceId)}
                    aria-label={t('task.approvedList.viewCta', { title: row.title })}
                  >
                    {t('task.approvedList.view')}
                  </AppButton>
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
};
